import React, { FunctionComponent, useState } from "react";
import { Form } from "antd";
import { HiOutlineCamera, HiOutlineClock, HiOutlineLocationMarker, HiOutlineRefresh } from "react-icons/hi";
import FormLabel from "./FormLabel";
import GeoCameraModal from "./GeoCameraModal";
import { GeoPictureValue, QuestionComponentProps } from "../helper";
import { buildRules } from "../formUtils";
import { formatCaptureTime, formatCoordinates } from "./captureFormat";
import { buildGeoCaptureAssetKey, uploadToS3 } from "./s3Upload";

interface GeoPictureInputProps {
  value?: GeoPictureValue;
  onChange?: (value: GeoPictureValue | undefined) => void;
  disabled?: boolean;
  formId?: string;
  questionId?: string;
}

/**
 * Controlled geo-picture input. As the direct child of a `Form.Item`, antd injects `value`/`onChange`,
 * so the stored value is `{ url, latitude, longitude, capturedAt }`; `buildResponses` later splits it
 * into the answer (the URL) and `otherFields` (the rest).
 *
 * The photo is taken in `GeoCameraModal`, which owns the camera. The answer is set only once the
 * photo and the coordinates are both in hand, so a saved answer never has one without the other.
 */
const GeoPictureInput: FunctionComponent<GeoPictureInputProps> = ({ value, onChange, disabled, formId, questionId }) => {
  const [cameraOpen, setCameraOpen] = useState(false);

  const upload = (blob: Blob, filename: string) => uploadToS3(blob, buildGeoCaptureAssetKey(formId, questionId, filename), blob.type);

  const handleCaptured = (captured: GeoPictureValue) => {
    onChange?.(captured);
    setCameraOpen(false);
  };

  // Place names are switched off (Rev 69): no lookup. The learner sees "Location recorded"; read-only views show the coordinates.
  const hasLocation = typeof value?.latitude === "number" && typeof value?.longitude === "number";
  const capturedAtLabel = formatCaptureTime(value?.capturedAt);

  if (value?.url) {
    return (
      <div className="overflow-hidden rounded-xl border border-solid border-neutral-5">
        {/* Large enough to actually check the photo before submitting — a thumbnail is not reviewable. */}
        <img src={value.url} alt="Captured" className="block max-h-80 w-full bg-neutral-3 object-cover" />
        <div className="flex items-center justify-between gap-3 bg-neutral-2 px-4 py-3">
          <div className="flex min-w-0 flex-col gap-1">
            <span className="inline-flex items-center gap-1.5 text-sm text-neutral-11">
              <HiOutlineLocationMarker className="flex-shrink-0 text-base" />
              <span className="truncate">
                {hasLocation ? (disabled ? formatCoordinates(value?.latitude, value?.longitude) : "Location recorded") : "Location not recorded"}
              </span>
            </span>
            {capturedAtLabel && (
              <span className="inline-flex items-center gap-1.5 text-xs text-neutral-9">
                <HiOutlineClock className="flex-shrink-0 text-sm" />
                {capturedAtLabel}
              </span>
            )}
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={() => setCameraOpen(true)}
              className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-md border border-solid border-neutral-5 bg-white px-3 py-2 text-sm text-neutral-11 cursor-pointer hover:text-neutral-13"
            >
              <HiOutlineRefresh className="text-base" />
              Retake
            </button>
          )}
        </div>
        <GeoCameraModal open={cameraOpen} onCancel={() => setCameraOpen(false)} upload={upload} onCaptured={handleCaptured} />
      </div>
    );
  }

  return (
    <div>
      {/* Empty state is the primary action, not a small button lost in the form. */}
      <button
        type="button"
        onClick={() => setCameraOpen(true)}
        disabled={disabled}
        className="flex w-full flex-col items-center gap-2 rounded-xl border border-dashed border-neutral-6 bg-neutral-2 px-4 py-8 text-center cursor-pointer hover:border-neutral-8 disabled:cursor-not-allowed"
      >
        <HiOutlineCamera className="text-3xl text-neutral-9" />
        <span className="text-sm font-medium text-neutral-12">Take the photo</span>
        <span className="text-xs text-neutral-9">Opens your camera and records where you are</span>
      </button>
      <GeoCameraModal open={cameraOpen} onCancel={() => setCameraOpen(false)} upload={upload} onCaptured={handleCaptured} />
    </div>
  );
};

// GEO_PICTURE — learner captures a photo; the answer is its URL, the coordinates ride in otherFields.
const GeoPicture: FunctionComponent<QuestionComponentProps> = ({ question, disabled = false }) => (
  <Form.Item
    name={question.id}
    label={<FormLabel label={question.label} required={question.isRequired} description={question.description} />}
    rules={buildRules(question)}
  >
    <GeoPictureInput disabled={disabled} formId={question.formId} questionId={question.id} />
  </Form.Item>
);

export default GeoPicture;
